import { ListItem, ListItemIcon, ListItemText, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { differenceInMinutes, parseISO } from "date-fns";

import { TimeRangeLabel } from "../../../DateTime/TimeRangeLabel";
import { formatDayOfMonthWithDayName } from "../../../utils/formatDayOfMonthWithDayName";
import { formatHoursDurationCompact } from "../../../utils/formatHoursDurationCompact";
import { type TimeSlot } from "../../Open/types";
import { iconTypeByTimeSlotFilled } from "../../TimeSlotIcon";

interface SelectableShiftInfoListItemProps {
  shiftId: string;
  timeSlot: TimeSlot;
  startIso: string;
  endIso: string;
  payInMinorUnits: number | undefined;
  workplaceTimezone?: string;
  isSelected: boolean;
  isDisabled: boolean;
  disabledReason?: string;
  onToggle: (shiftId: string) => void;
}

export function SelectableShiftInfoListItem(props: SelectableShiftInfoListItemProps) {
  const {
    shiftId,
    timeSlot,
    startIso,
    endIso,
    payInMinorUnits,
    workplaceTimezone,
    isSelected,
    isDisabled,
    disabledReason,
    onToggle,
  } = props;

  const startDate = parseISO(startIso);
  const endDate = parseISO(endIso);
  const formattedDate = formatDayOfMonthWithDayName(startDate, { timezone: workplaceTimezone });
  const durationInHours = differenceInMinutes(endDate, startDate) / 60;
  const duration = formatHoursDurationCompact(durationInHours, { omitZeroMinutes: true });

  return (
    <Box
      component="button"
      disabled={isDisabled}
      sx={(theme) => ({
        display: "block",
        width: "100%",
        paddingX: 5,
        paddingY: 0,
        border: `2px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
        borderRadius: theme.shape?.borderRadius ?? 2,
        backgroundColor: theme.palette.common.white,
        boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.08)",
        cursor: isDisabled ? "default" : "pointer",
        opacity: isDisabled ? 0.5 : 1,
        transition: "border-color 0.15s ease-in-out",
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      })}
      onClick={() => {
        if (!isDisabled) {
          onToggle(shiftId);
        }
      }}
    >
      <ListItem>
        <ListItemIcon
          iconType={iconTypeByTimeSlotFilled[timeSlot]}
          color={(theme) => theme.palette.text.primary}
        />
        <ListItemText
          disableTypography
          primary={
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Text variant="body2">
                {isDisabled && isDefined(disabledReason) ? (
                  <>
                    <Text inline variant="body2">
                      {disabledReason}
                    </Text>
                    {` \u00B7 `}
                    <Text inline semibold variant="body2">
                      {formattedDate}
                    </Text>
                  </>
                ) : (
                  <>
                    <Text inline semibold variant="body2">
                      {formattedDate}
                    </Text>
                    {` \u00B7 ${duration}`}
                  </>
                )}
              </Text>
              {isDefined(payInMinorUnits) && (
                <Text semibold variant="body2">
                  {formatCentsAsUsd(payInMinorUnits)}
                </Text>
              )}
            </Stack>
          }
          secondary={
            <TimeRangeLabel
              semibold
              variant="body1"
              dateRange={{ startDate, endDate }}
              timezone={workplaceTimezone}
            />
          }
        />
      </ListItem>
    </Box>
  );
}
